import { useId, type ReactNode } from 'react';

/**
 * Чекбокс согласий для формы регистрации (PRD §2.2; P-017 + P-035).
 * Block 0 Phase 2 / Step 3.
 *
 * Поддерживает богатый label через `children` (`ReactNode`), чтобы можно было
 * вставлять inline `<a>` на placeholder-страницы Cookie-Policy /
 * User-Agreement (Q9):
 *
 * ```tsx
 * <ConsentCheckbox name="dataProcessing" checked={...} onChange={...}>
 *   Я согласен на <a href="/legal/user-agreement" target="_blank">обработку
 *   персональных данных</a>
 * </ConsentCheckbox>
 * ```
 *
 * P-022 a11y baseline:
 *   - `<input type="checkbox" id={id}>` обёрнут в `<label htmlFor={id}>` так,
 *     что нативный клик на тексте лейбла переключает чекбокс. Однако клик на
 *     inline `<a>` НЕ должен переключать чекбокс — это handled через
 *     `onClick={e.stopPropagation()}` на якоре в Container коде (см. Step 4).
 *     На уровне примитива поведение нативное: всё внутри label является
 *     click-target.
 *   - aria-invalid + aria-describedby при error (например, «Согласие
 *     обязательно»).
 *   - min-h-tap (44px) на label — кликабельная зона целиком включает чекбокс
 *     и текст.
 *
 * Намеренно нет prop `hint`: для согласий подсказка не помещается под чекбокс
 * без потери compactness формы; объяснения встраиваются в сам label через
 * `children` (ReactNode, поддерживает `<a>` и `<span>` со стилями). Это
 * расхождение с TextInput/PhoneInput/Select сознательное.
 */
export interface ConsentCheckboxProps {
  name: string;
  /** Текст согласия (`ReactNode`, чтобы поддержать inline `<a>`). */
  children: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  error?: string;
  disabled?: boolean;
  required?: boolean;
}

export function ConsentCheckbox({
  name,
  children,
  checked,
  onChange,
  error,
  disabled,
  required,
}: ConsentCheckboxProps) {
  const reactId = useId();
  const inputId = `${name}-${reactId}`;
  const errorId = `${inputId}-error`;
  const hasError = Boolean(error);

  return (
    <div className="flex flex-col">
      <label
        htmlFor={inputId}
        className={
          'flex items-start gap-3 min-h-tap cursor-pointer ' +
          (disabled ? 'opacity-60 cursor-not-allowed' : '')
        }
      >
        <input
          id={inputId}
          name={name}
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
          required={required}
          aria-invalid={hasError || undefined}
          aria-describedby={hasError ? errorId : undefined}
          className={
            'mt-1 w-5 h-5 shrink-0 rounded ' +
            'border-[1.5px] ' +
            (hasError ? 'border-error ' : 'border-accent ') +
            'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none ' +
            'accent-cta'
          }
        />
        <span className="text-ink text-base leading-snug">{children}</span>
      </label>
      {hasError ? (
        <p id={errorId} role="alert" className="text-error text-sm mt-1">
          {error}
        </p>
      ) : null}
    </div>
  );
}
